import RefreshRuntime from 'react-refresh';

/**
 * Initializes the React Refresh runtime for hot module replacement (HMR).
 *
 * React Refresh enables fast refresh of React components without losing their state.
 * This function sets up the necessary global hooks and functions that the bundler-transformed
 * code will use to register components and trigger updates.
 *
 * This should be called once during application initialization, before any React components are loaded.
 *
 * @example
 * // In your app entry point:
 * import { registerReactRefresh } from '@ms-cloudpack/overlay';
 * registerReactRefresh();
 */
export const registerReactRefresh = (): void => {
  // Initialize React Refresh by injecting into React's internals
  // This hooks into React's reconciler to enable hot reloading capabilities
  // It modifies the global React DevTools hook to track component updates
  RefreshRuntime.injectIntoGlobalHook(window);

  // Set up the component registration function
  // This function is called by bundler-injected code for every React component/hook
  //
  // Example of what the bundler transforms:
  // Original code:
  //   export const MyComponent = () => <div>Hello</div>
  //
  // Transformed code:
  //   export const MyComponent = () => <div>Hello</div>
  //   $RefreshReg$(MyComponent, 'MyComponent');
  //
  // The registration allows React Refresh to:
  // - Track which components exist in the module
  // - Match old and new versions of components during updates
  // - Determine if a full reload is needed (e.g., if hooks changed)
  window.$RefreshReg$ = (type: unknown, id: string) => {
    // TODO: Make this fully qualified with id including package name to avoid collisions
    // Currently, if two packages export a component with the same name, they could collide
    // Example collision: '@pkg-a/MyComponent' and '@pkg-b/MyComponent' both register as 'MyComponent'
    RefreshRuntime.register(type, id);
  };

  // Set up the signature function for tracking hook changes
  // This is used to detect when hooks in a component have changed
  //
  // Example of what the bundler transforms:
  // Original code:
  //   const MyComponent = () => {
  //     const [count, setCount] = useState(0);
  //     return <div>{count}</div>
  //   }
  //
  // Transformed code:
  //   var _s = $RefreshSig$();
  //   const MyComponent = () => {
  //     _s();  // Signature call to track hooks
  //     const [count, setCount] = useState(0);
  //     return <div>{count}</div>
  //   }
  //   _s(MyComponent, "useState{count}");
  //
  // If hook signatures change (e.g., adding/removing useState), React Refresh will:
  // - Detect the change via the signature mismatch
  // - Force a full remount of the component (state will be lost)
  // - Warn the developer about the limitation
  window.$RefreshSig$ = () => {
    return RefreshRuntime.createSignatureFunctionForTransform();
  };

  // Store the runtime globally for manual refresh triggering
  // This is used by handlePackageUpdate.ts to call performReactRefresh()
  // after new modules are imported
  //
  // The runtime provides:
  // - performReactRefresh(): Triggers the actual component updates
  // - register(): Called by $RefreshReg$ to track components
  // - createSignatureFunctionForTransform(): Called by $RefreshSig$ to track hooks
  window.$RefreshRuntime$ = RefreshRuntime;

  console.log('[React Refresh] Runtime initialized');
};
